/* eslint-disable react-hooks/exhaustive-deps */
import { AxiosError } from "axios";
import { useRouter, useSearchParams } from "next/navigation";
import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";

import { useWizard } from "@/app/shared-providers/wizard-context-provider";
import { Brand, ContactEdit, RenewalPage } from "@/config/app-constant";
import { APPLICATION } from "@/constants";
import { useGtmContactDetailsOptions } from "@/hooks/gtm/contact-details/use-gtm-contact-details-options";
import useMediaQuery from "@/hooks/use-media-query/use-media-query-enhanced";
import { useLoading } from "@/providers/loading-provider";
import { RootState } from "@/store/root-reducer";
import { updateContactDetails } from "@/store/slices/contact-details.slice";
import { setApiError } from "@/store/slices/errors/api-error.slice";
import { updateQuote } from "@/store/slices/quote/quote.slice";
import { brand } from "@/tailwind.config";
import { PayloadType } from "@/types/ContactDetailsTypes";
import { apiErrorMappings } from "@/utils/errors/api-error-map";
import { contactDetailsDataMap } from "@/utils/gtm/contact-details-data-map";
import handleError, { ErrorResponse } from "@/utils/handle-errors";
import { travelFromCountryData } from "@/utils/question-location-from.utils";

import useSaveContactDetails from "./use-save-contact-details";
import {
  ContactFormHookProps,
  ContactMethod,
  ContactMethodForExpat,
  SetErrorFunction,
  ValidatorFunction,
} from "../../cover-for/types/cover-for.type";
import {
  dropdownDataValues,
  emailRegexValidation,
  emailValidation,
  isContactPageShiftFlowExperimentOn,
  phoneNumberValidation,
  removeCountryCode,
} from "../utils/contact-details-page.util";

export function useContactForm({
  emailLabelIsEmpty,
  emailLabelIsInvalid,
  contactFormContent,
  mobileLabelIsEmpty,
  mobileLabelIsInvalid,
}: ContactFormHookProps) {
  const { current } = useWizard();

  // Compute navigation behavior flag once
  const navigateBeforeApiComplete = useMemo(
    () =>
      sessionStorage.getItem("navigateToQuoteBeforeContactDetailsApi") ===
      "true",
    []
  );

  const contactPreferences = current?.brandSpecificParams
    ?.showAllMarketingConsentField
    ? [
        (contactFormContent?.ctaLabelOneId as ContactMethod) ?? "email",
        (contactFormContent?.ctaLabelTwoId as ContactMethod) ?? "sms",
        (contactFormContent?.ctaLabelThreeId as ContactMethod) ?? "telephone",
        (contactFormContent?.ctaLabelFourId as ContactMethod) ?? "post",
      ]
    : [
        (contactFormContent?.ctaLabelOneId as ContactMethodForExpat) ?? "email",
        (contactFormContent?.ctaLabelTwoId as ContactMethodForExpat) ??
          "telephone",
      ];

  const contactPreferenceLabels = current?.brandSpecificParams
    ?.showAllMarketingConsentField
    ? [
        contactFormContent?.ctaLabelOne,
        contactFormContent?.ctaLabelTwo,
        contactFormContent?.ctaLabelThree,
        contactFormContent?.ctaLabelFour,
      ]
    : [contactFormContent?.ctaLabelOne, contactFormContent?.ctaLabelTwo];
  const emailRef = useRef<HTMLButtonElement | null>(null);
  const phoneRef = useRef<HTMLButtonElement | null>(null);
  const quoteResponse = useSelector((state: RootState) => state.quote);
  const { referenceId, attributes } = quoteResponse;
  const router = useRouter();
  const dispatch = useDispatch();
  const searchParams = useSearchParams();
  const editPage = searchParams.get(ContactEdit);
  const [email, setEmail] = React.useState("");
  const [phone, setPhone] = React.useState("");
  const [errorEmailMessage, setErrorEmailMessage] = React.useState("");
  const [isTyping, setIsTyping] = React.useState(false);
  const [invalidPhoneMessage, setInvalidPhoneMessage] = React.useState("");
  const [telCountryCode, setTelCountryCode] = React.useState<string>("");
  const isTablet = useMediaQuery("(min-width: 600px)");
  const [isPreferenceClicked, setIsPreferenceClicked] = React.useState(false);
  const [continueIsLoading, setContinueIsLoading] = React.useState(false);
  const [modalOpen, setModalOpen] = React.useState(false);
  const [isGtmReady, setIsGtmReady] = React.useState(false);
  const [preferences, setPreferences] = React.useState(() =>
    current?.brandSpecificParams?.showAllMarketingConsentField
      ? {
          [contactPreferences[0]]: false,
          [contactPreferences[1]]: false,
          [contactPreferences[2]]: false,
          [contactPreferences[3]]: false,
        }
      : {
          [contactPreferences[0]]: false,
          [contactPreferences[1]]: false,
        }
  );

  const ukValueForDropdown = dropdownDataValues.filter(
    (opt: { value: string }) => opt.value === "+44"
  );

  const [dropDownListToDisplay, setDropDownListToDisplay] =
    React.useState<Array<(typeof ukValueForDropdown)[number]>>(
      ukValueForDropdown
    );

  const [updateContact, setUpdateContact] = React.useState(false);

  const [isOpenUnsavedChangesModal, setIsOpenUnsavedChangesModal] =
    React.useState(false);

  const phoneRegex = current?.brandSpecificParams?.phoneNumberRegex as Record<
    string,
    RegExp
  >;

  const phoneNumberRegex = useMemo(
    () => new RegExp(phoneRegex[telCountryCode] ?? phoneRegex?.DEFAULT ?? ""),
    [telCountryCode, phoneRegex]
  );

  const { showLoading, hideLoading } = useLoading();

  const resetTrigger = useCallback(() => {
    setUpdateContact(false);
    setContinueIsLoading(false);
    if (APPLICATION === "renewal") {
      hideLoading();
    }
  }, []);
  const { data, loading, error, saveContactDetails } =
    useSaveContactDetails(resetTrigger);

  const isCtaActiveWhenTyping = useMemo(() => {
    return (
      email.trim() !== "" &&
      emailRegexValidation.test(email.trim()) &&
      phone.trim() !== "" &&
      phoneNumberRegex.test(phone.split(" ").join(""))
    );
  }, [email, phone]);

  const isEmpty = useMemo(() => {
    return (
      email === "" ||
      errorEmailMessage !== "" ||
      invalidPhoneMessage !== "" ||
      phone === ""
    );
  }, [email, errorEmailMessage, invalidPhoneMessage, phone]);

  let userProvidedData = null;
  if (data) {
    userProvidedData = contactDetailsDataMap(data.attributes);
  }

  useGtmContactDetailsOptions({
    userDetails: userProvidedData,
    isReady: isGtmReady,
  });

  useEffect(() => {
    const { organiser, marketingConsent } = attributes ?? {};
    const fromLocation = attributes.tripDetails?.fromLocation;

    let countryObjFromLoc: (typeof ukValueForDropdown)[number] | undefined;

    if (fromLocation) {
      countryObjFromLoc = dropdownDataValues.find(
        (selectedCountry) =>
          selectedCountry.optDropdownTelCode ===
          travelFromCountryData.find((country) => country.code === fromLocation)
            ?.telCountryCode
      );

      if (countryObjFromLoc) {
        setTelCountryCode(countryObjFromLoc.optDropdownTelCode);
      }
    }

    if (countryObjFromLoc) {
      const combinedArray = [...ukValueForDropdown, countryObjFromLoc];
      setDropDownListToDisplay(combinedArray);
    }
    if (organiser?.email) {
      setEmail(organiser.email);
      if (!emailRegexValidation.test(organiser.email.trim())) {
        setErrorEmailMessage(contactFormContent?.emailLabelIsInvalid ?? "");
      }
    }

    if (organiser?.telephone) {
      const phoneNumber = organiser.telephone.split(" ").join("");
      const cleanedPhoneNumber: string = removeCountryCode(
        phoneNumber,
        setTelCountryCode
      );
      setPhone(
        brand === Brand.EXPAT ? cleanedPhoneNumber : organiser?.telephone
      );
      if (
        !phoneNumberRegex.test(
          brand === Brand.EXPAT ? cleanedPhoneNumber : organiser?.telephone
        )
      ) {
        setInvalidPhoneMessage(contactFormContent?.mobileLabelIsInvalid ?? "");
      }
    }

    if (marketingConsent) {
      setPreferences(
        current?.brandSpecificParams?.showAllMarketingConsentField
          ? {
              [contactPreferences[0]]:
                marketingConsent[contactPreferences[0]] ?? true,
              [contactPreferences[1]]:
                marketingConsent[contactPreferences[1]] ?? true,
              [contactPreferences[2]]:
                marketingConsent[contactPreferences[2]] ?? true,
              [contactPreferences[3]]:
                marketingConsent[contactPreferences[3]] ?? true,
            }
          : {
              [contactPreferences[0]]:
                marketingConsent[contactPreferences[0]] ?? true,
              [contactPreferences[1]]:
                marketingConsent[contactPreferences[1]] ?? true,
            }
      );
    }
  }, [attributes, contactFormContent, dropdownDataValues]);

  useEffect(() => {
    const timeOut = setTimeout(() => {
      const preference = document.getElementById(
        "contact-page-preferences-hyperlink"
      );
      const handleClick = (event: MouseEvent) => {
        event.preventDefault();
        setIsPreferenceClicked((prev) => !prev);
      };
      if (preference) {
        preference.onclick = handleClick;
      }
    }, 500);
    return () => clearTimeout(timeOut);
  }, [referenceId]);

  useEffect(() => {
    const handleEffect = async () => {
      if (!loading && data && !error) {
        dispatch(updateQuote(data));
        setIsGtmReady(true);
        // Only navigate if we haven't already navigated (old flow or edit/renewal flows)
        if (editPage) {
          router.push(current.edges.editFlowNext ?? "");
        } else if (APPLICATION === RenewalPage) {
          router.push("/");
        } else if (!navigateBeforeApiComplete) {
          // Original flow: Navigate after API completes (no interstitial needed)
          router.push(current.next ?? "");
        }
        // If navigateBeforeApiComplete is true, we already navigated in handleContinue
        // Just update Redux here, quote page will react to the data update
      } else if (!loading && error !== null) {
        if (APPLICATION === RenewalPage) {
          await handleError(
            error as AxiosError<ErrorResponse>,
            router,
            dispatch
          );
        } else if (error.response?.status === 401) {
          router.push("/");
        } else {
          const errorObject = await apiErrorMappings(error);
          errorObject.multiButton = true;
          dispatch(setApiError(errorObject));
        }
      }
    };
    handleEffect();
  }, [
    data,
    loading,
    error,
    dispatch,
    router,
    editPage,
    APPLICATION,
    navigateBeforeApiComplete,
  ]);

  useEffect(() => {
    dispatch(
      updateContactDetails({
        isFilled: !(isTyping ? !isCtaActiveWhenTyping : isEmpty),
      })
    );
  }, [isEmpty, isCtaActiveWhenTyping, isTyping]);

  const handleEmailChange = useCallback((address: string) => {
    setEmail(address);
    setIsTyping(true);
    if (errorEmailMessage !== "") setErrorEmailMessage("");
  }, []);

  const handleMobileNumberChange = useCallback((mobile: string) => {
    setPhone(mobile);
    setIsTyping(true);
    if (invalidPhoneMessage !== "") setInvalidPhoneMessage("");
  }, []);

  const handleBlur = useCallback(
    (
      validator: ValidatorFunction,
      value: string,
      setError: SetErrorFunction
    ) => {
      setIsTyping(false);
      // Validation should trigger only if the user entered a value
      if (value && validator(value)) setError("");
    },
    [phone, email]
  );

  // email validation
  const isEmailValid = useCallback(
    (value: string) => {
      return emailValidation(
        value,
        emailLabelIsEmpty,
        emailLabelIsInvalid,
        setErrorEmailMessage
      );
    },
    [emailLabelIsEmpty, emailLabelIsInvalid, setErrorEmailMessage]
  );

  const isPhoneNumberValid = useCallback(
    (value: string) => {
      return phoneNumberValidation(
        value,
        phoneNumberRegex,
        mobileLabelIsEmpty,
        mobileLabelIsInvalid,
        setInvalidPhoneMessage
      );
    },
    [phoneNumberRegex, mobileLabelIsEmpty, mobileLabelIsInvalid]
  );

  const handleOptionSelect = useCallback(
    (option: (typeof dropdownDataValues)[number]) => {
      const newCountryCode = option.optDropdownTelCode;
      setTelCountryCode(newCountryCode);

      // Re-validate with new country code's regex if phone number is not empty
      const formattedNumber = phone.trim();
      if (formattedNumber !== "") {
        const newRegex = new RegExp(
          phoneRegex[newCountryCode] ?? phoneRegex?.DEFAULT ?? ""
        );
        if (!newRegex.test(phone.replace(/\s/g, ""))) {
          setInvalidPhoneMessage(mobileLabelIsInvalid);
        } else {
          setInvalidPhoneMessage("");
        }
      }
    },
    [phone, phoneRegex, mobileLabelIsInvalid]
  );

  const handleBack = useCallback(() => {
    let backMedicalDashboard = false;
    const questionOrderVariant = isContactPageShiftFlowExperimentOn();
    // handle back
    if (APPLICATION === RenewalPage) {
      setIsOpenUnsavedChangesModal(true);
    } else if (questionOrderVariant) {
      const isIndividualAndOrganizerTravelling =
        attributes.organiser?.travelling &&
        attributes.tripDetails?.partyType === "INDIVIDUAL";
      router.push(
        isIndividualAndOrganizerTravelling
          ? current.edges.backToOrganiser ?? ""
          : current.edges.backToTravellers ?? ""
      );
    } else {
      const { travellerDetails } = attributes;
      if (travellerDetails) {
        const { traveller } = travellerDetails;
        if (traveller?.length) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          traveller.forEach((trav: any) => {
            if (trav.medical !== undefined) {
              backMedicalDashboard = true;
            }
          });
        }
      }

      if (backMedicalDashboard) {
        router.push(current.edges.backToMedicalDashboard ?? "");
      } else {
        router.push(current.edges.backToMedicalTreatments ?? "");
      }
    }
  }, [attributes?.travellerDetails]);

  const handleUnSavedWarningMessageContinueBtn = useCallback(() => {
    const { organiser, marketingConsent } = attributes ?? {};
    if (organiser) {
      setEmail(organiser.email ?? "");
      setPhone(organiser.telephone ?? "");
    }

    if (marketingConsent) {
      setPreferences({
        [contactPreferences[0]]:
          marketingConsent[contactPreferences[0] ?? true],
        [contactPreferences[1]]:
          marketingConsent[contactPreferences[1] ?? true],
        [contactPreferences[2]]:
          marketingConsent[contactPreferences[2] ?? true],
        [contactPreferences[3]]:
          marketingConsent[contactPreferences[3] ?? true],
      });
    }
    setIsOpenUnsavedChangesModal(false);
    router.push("/");
  }, []);

  const handleCancelEdit = useCallback(() => {
    setModalOpen(true);
  }, []);

  const validation = () => {
    let isValid = true;

    const emailValid = emailValidation(
      email,
      emailLabelIsEmpty,
      emailLabelIsInvalid,
      setErrorEmailMessage
    );
    if (!emailValid) isValid = false;

    const phoneValid = isPhoneNumberValid(phone);
    if (!phoneValid) isValid = false;

    return isValid;
  };

  const handleContinue = useCallback(async () => {
    if (!validation()) return;
    if (updateContact) return;
    const formatPhoneNumber =
      current?.brandSpecificParams?.formatMobileNumber && phone.startsWith("0")
        ? phone.slice(1)
        : phone;

    const telWithCountryCode =
      brand === Brand.EXPAT
        ? telCountryCode.concat(formatPhoneNumber.split(" ").join(""))
        : formatPhoneNumber;

    const getRetentionConsent =
      current?.brandSpecificParams?.getRetentionConsent;

    const payLoad: PayloadType = {
      marketingConsent: current?.brandSpecificParams
        ?.showAllMarketingConsentField
        ? {
            [contactPreferences[0]]: preferences[contactPreferences[0]],
            [contactPreferences[1]]: preferences[contactPreferences[1]],
            [contactPreferences[2]]: preferences[contactPreferences[2]],
            [contactPreferences[3]]: preferences[contactPreferences[3]],
          }
        : {
            [contactPreferences[0]]: preferences[contactPreferences[0]],
            [contactPreferences[1]]: preferences[contactPreferences[1]],
          },

      email: email.split(" ").join(""),
      telephone: telWithCountryCode,
      ...(getRetentionConsent && { quoteDataHoldingConsent: true }),
    };

    // Check feature flag for instant navigation behavior
    if (navigateBeforeApiComplete) {
      // New behavior: Navigate immediately and show interstitial while API completes
      setContinueIsLoading(true);
      setUpdateContact(true);
      // Clear session storage to trigger interstitial on quote page
      sessionStorage.removeItem("isSession");
      // Navigate to quote page immediately
      router.push(current.next ?? "");
      // Start API call in background (useEffect will handle response)
      saveContactDetails(payLoad);
    } else {
      // Original behavior: Wait for API to complete before navigation
      saveContactDetails(payLoad);
      setContinueIsLoading(true);
      showLoading();
    }
  }, [
    email,
    phone,
    telCountryCode,
    preferences,
    updateContact,
    navigateBeforeApiComplete,
    editPage,
    router,
    current,
  ]);

  const flags = {
    continueIsLoading,
    editPage,
    isOpenUnsavedChangesModal,
    isPreferenceClicked,
    isTablet,
    modalOpen,
    isLoading: loading,
  };

  const formErrors = {
    errorEmailMessage,
    invalidPhoneMessage,
  };

  const formValues = {
    contactPreferenceLabels,
    contactPreferences,
    dropDownListToDisplay,
    email,
    phone,
    preferences,
    telCountryCode,
  };

  const handlers = {
    handleBack,
    handleBlur,
    handleCancelEdit,
    isHideFooterBack: APPLICATION !== RenewalPage && !!editPage,
    handleContinue,
    handleEmailChange,
    handleMobileNumberChange,
    handleOptionSelect,
    handleUnSavedWarningMessageContinueBtn,
  };

  const navigation = {
    router,
  };

  const refs = {
    emailRef,
    phoneRef,
  };

  const setters = {
    setErrorEmailMessage,
    setInvalidPhoneMessage,
    setIsOpenUnsavedChangesModal,
    setModalOpen,
    setPreferences,
  };

  const validations = {
    isCtaActiveWhenTyping,
    isEmailValid,
    isEmpty,
    isPhoneNumberValid,
    isTyping,
  };

  return {
    flags,
    formErrors,
    formValues,
    handlers,
    navigation,
    refs,
    setters,
    validations,
  };
}
